// index.ts
import axios from 'axios';
// eslint-disable-next-line no-duplicate-imports
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { formatHumpLineTransfer, getUrlByPromise } from '@/utils/tools';
type Result<T> = {
  statusCode: number;
  statusMsg: string;
  data: T;
  result: T;
};

// 导出Request类，可以用来自定义传递配置来创建实例
export class Request {
  // axios 实例
  instance: AxiosInstance;
  // 基础配置，url和超时时间
  baseConfig: AxiosRequestConfig = {
    baseURL: '/',
    timeout: 6000,
    withCredentials: true,
  };
  constructor(config: AxiosRequestConfig) {
    // 使用axios.create创建axios实例
    this.instance = axios.create(Object.assign(this.baseConfig, config));

    this.instance.interceptors.request.use(
      // eslint-disable-next-line no-shadow
      async (reqconfig: AxiosRequestConfig) => {
        // 一般会请求拦截里面加token，用于后端的验证
        const token = localStorage.getItem('token') as string;
        if (token) {
          reqconfig.headers!.Authorization = token;
        }
        reqconfig.url = await getUrlByPromise(reqconfig.url);
        return reqconfig;
      },
      // 请求错误，这里可以用全局提示框进行提示
      (err: any) => Promise.reject(err)
    );

    this.instance.interceptors.response.use(
      // 直接返回res，当然你也可以只返回res.data 系统如果有自定义code也可以在这里处理
      (res: AxiosResponse) => formatHumpLineTransfer(res.data),
      // 这里用来处理http常见错误，进行全局提示这里是AxiosError类型，所以一般我们只reject我们需要的响应即可
      (err: any) => Promise.reject(err.response)
    );
  }

  // 定义请求方法
  public request(config: AxiosRequestConfig): Promise<AxiosResponse> {
    return this.instance.request(config);
  }

  public get<T = any>(url: string, config?: AxiosRequestConfig): Promise<Result<T>> {
    return this.instance.get(url, config);
  }

  public post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<Result<T>>> {
    return this.instance.post(url, data, config);
  }

  public put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<Result<T>>> {
    return this.instance.put(url, data, config);
  }

  public delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<Result<T>> {
    return this.instance.delete(url, config);
  }
  /**
   * jsonp请求封装
   * @param url 请求地址
   * @param data 请求数据
   */
  async jsonp<T = any>(url: string, data: any): Promise<Result<T>> {
    const domainUrl = await getUrlByPromise(url);
    return new Promise((resolve, reject) => {
      window.$.ajax({
        data,
        type: 'get',
        url: domainUrl,
        async: false,
        dataType: 'jsonp',
        timeout: 3000,
        success: function success(res: any) {
          resolve(res);
        },
        error: function error(err: any) {
          reject(err);
        },
      });
    });
  }
}

// 默认导出Request实例
export default new Request({});
